/* Sampling — moved out of pages/Sampling.jsx in split step 6d (28-Oct-2026), unchanged. */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import { asArray } from "@/lib/asArray";
import api from "@/lib/api";
import { fmtDateTimeFirm } from "@/lib/dt";   // 5c · F-5
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { DialogDescription } from "@/components/ui/dialog";
import { Download, ArrowUpRight } from "lucide-react";
import FullScreenDialog from "@/components/FullScreenDialog";
import { fmtNumber, fmtCount } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

// ------------------------------------------------------------------
// Skipped-rows dialog — surfaces the (capped) per-row skip log for a
// head-wise upload so the auditor can eyeball exactly which rows the
// parser dropped and why (out-of-scope FS mapping, missing code, bad
// row, etc.). Backed by GET /gl/{eng}/head-uploads/{id}/skipped.
// ------------------------------------------------------------------
function SkippedRowsDialog({ open, onOpenChange, engagementId, uploadId }) {
  const colW = useResizableColumns({ storageKey: "sampling-skipped-rows-dialog", defaults: [160, 110, 240, 110, 130, 130, 240], mins: [100, 72, 130, 72, 90, 90, 130] });   // Step 11d · add-on
  const { t, lang, dict } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !engagementId || !uploadId) return;
    setLoading(true);
    api.get(`/gl/${engagementId}/head-uploads/${uploadId}/skipped`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, engagementId, uploadId]);

  const REASON_LABEL = {
    out_of_scope: t("sampling.reasonLabels.out_of_scope"),
    unmapped_on_engagement: t("sampling.reasonLabels.unmapped_on_engagement"),
    missing_code: t("sampling.reasonLabels.missing_code"),
    bad_row: t("sampling.reasonLabels.bad_row"),
  };

  return (
    <FullScreenDialog open={open} onOpenChange={onOpenChange} testid="skipped-rows-dialog"
                      title={t("sampling.skippedRowsTitle")}>
          <DialogDescription className="mb-2">
            {loading ? t("sampling.skippedLoading") : data ? (
              <>
                <b>{data.source_file || "—"}</b> · uploaded {fmtDateTimeFirm(data.uploaded_at)} ·
                {" "}{t("sampling.skippedFsHeads")} {asArray(data.fs_codes).join(", ") || "—"}
              </>
            ) : "—"}
          </DialogDescription>

        {data && (
          <>
            {(data.totals.now_fixable || 0) > 0 && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
                   data-testid="skipped-fixable-banner">
                {t("sampling.skippedFixableBanner").replace("{n}", fmtPlural(data.totals.now_fixable, lang, dict.plurals.rows))}
              </div>
            )}
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="rounded border bg-slate-50 p-2 dark:bg-slate-900/40">
                <div className="text-[10px] uppercase text-muted-foreground">{t("sampling.outOfScope")}</div>
                <div className="text-lg font-semibold text-amber-700">
                  {fmtCount(data.totals.out_of_scope || 0)}
                </div>
              </div>
              <div className="rounded border bg-slate-50 p-2 dark:bg-slate-900/40">
                <div className="text-[10px] uppercase text-muted-foreground">{t("sampling.missingCode")}</div>
                <div className="text-lg font-semibold text-amber-700">
                  {fmtCount(data.totals.missing_code || 0)}
                </div>
              </div>
              <div className="rounded border bg-slate-50 p-2 dark:bg-slate-900/40">
                <div className="text-[10px] uppercase text-muted-foreground">{t("sampling.badRow")}</div>
                <div className="text-lg font-semibold text-amber-700">
                  {fmtCount(data.totals.bad_row || 0)}
                </div>
              </div>
              <div className="rounded border bg-emerald-50 p-2 dark:bg-emerald-950/30">
                <div className="text-[10px] uppercase text-muted-foreground">{t("sampling.totalSkipped")}</div>
                <div className="text-lg font-semibold text-emerald-700">
                  {fmtCount(data.totals.total_skipped || 0)}
                </div>
              </div>
            </div>

            <div className="table-sticky-box mt-3 overflow-x-auto rounded-md border">
              <ResizableTable cols={colW} className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-900">
                  <tr>
                    <ResizableTh index={0} className="px-2 py-1 text-start">{t("sampling.skippedCols.row")}</ResizableTh>
                    <ResizableTh index={1} className="px-2 py-1 text-start">{t("sampling.skippedCols.accountCode")}</ResizableTh>
                    <ResizableTh index={2} className="px-2 py-1 text-start">{t("sampling.skippedCols.accountName")}</ResizableTh>
                    <ResizableTh index={3} className="px-2 py-1 text-start">{t("sampling.skippedCols.voucher")}</ResizableTh>
                    <ResizableTh index={4} className="px-2 py-1 text-end">{t("sampling.skippedCols.debit")}</ResizableTh>
                    <ResizableTh index={5} className="px-2 py-1 text-end">{t("sampling.skippedCols.credit")}</ResizableTh>
                    <ResizableTh index={6} className="px-2 py-1 text-start">{t("sampling.skippedCols.reason")}</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).length === 0 && (
                    <tr><td colSpan={7} className="p-3 text-center text-muted-foreground">
                      {t("sampling.noRowDetail")}
                    </td></tr>
                  )}
                  {asArray(data.rows).map((r, i) => (
                    <tr key={i} className={`border-t ${r.now_fixable ? "bg-emerald-50/60 dark:bg-emerald-950/20" : ""}`}
                        data-testid={`skipped-row-${i}`}>
                      <td className="px-2 py-1 tabular-nums">{r.row_index || "—"}</td>
                      <td className="px-2 py-1 font-mono">{r.account_code || "—"}</td>
                      <td className="px-2 py-1">{r.account_name || "—"}</td>
                      <td className="px-2 py-1 font-mono">{r.voucher_no || "—"}</td>
                      <td className="px-2 py-1 text-end tabular-nums">
                        {typeof r.debit === "number" ? fmtNumber(r.debit) : "—"}
                      </td>
                      <td className="px-2 py-1 text-end tabular-nums">
                        {typeof r.credit === "number" ? fmtNumber(r.credit) : "—"}
                      </td>
                      <td className="px-2 py-1">
                        {r.now_fixable ? (
                          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                            {t("sampling.nowMappedTo").replace("{code}", r.now_mapped_to)}
                          </span>
                        ) : (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                            {REASON_LABEL[r.reason] || r.reason || "—"}
                          </span>
                        )}
                        {r.current_fs_mapping && !r.now_fixable && (
                          <span className="ms-1 text-[10px] text-muted-foreground">
                            {t("sampling.atUploadFs").replace("{code}", r.current_fs_mapping)}
                          </span>
                        )}
                        {r.now_mapped_to && !r.now_fixable && r.now_mapped_to !== r.current_fs_mapping && (
                          <span className="ms-1 text-[10px] text-muted-foreground">
                            {t("sampling.nowFs").replace("{code}", r.now_mapped_to)}
                          </span>
                        )}
                        {r.account_code && engagementId && !r.now_fixable && (
                          <button type="button"
                                  onClick={() => {
                                    onOpenChange(false);
                                    // Route is `/ifrs/:eng_id/mapping` (see App.js).
                                    window.location.assign(
                                      `/ifrs/${engagementId}/mapping?focus=${encodeURIComponent(r.account_code)}`);
                                  }}
                                  className="ms-2 inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 dark:bg-slate-900 dark:text-emerald-300"
                                  title={t("sampling.openFsMappingTitle").replace("{code}", r.account_code)}
                                  data-testid={`skipped-fix-${i}`}>
                            <ArrowUpRight className="h-3 w-3" /> {t("sampling.fixBtn")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
            </div>
            {data.capped && (
              <div className="text-[11px] text-muted-foreground">
                {t("sampling.showingFirst")
                  .replace("{n}", fmtCount((data.rows || []).length))
                  .replace("{r}", fmtCount((data.totals.total_skipped || 0) - (data.rows || []).length))}
              </div>
            )}
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="text-[11px] text-muted-foreground">
                {t("sampling.fixHint").split(/\{fsBold\}|\{replaceBold\}/).map((chunk, i, arr) => (
                  <React.Fragment key={i}>
                    {chunk}
                    {i === 0 && arr.length > 1 && <b>{t("sampling.fsMappingBtn")}</b>}
                    {i === 1 && arr.length > 2 && <b>{t("sampling.replace")}</b>}
                  </React.Fragment>
                ))}
              </div>
              {(data.rows || []).length > 0 && (
                <Button size="sm" variant="outline"
                        onClick={() => downloadSkippedCsv(data)}
                        data-testid="skipped-download-csv">
                  <Download className="me-1 h-3 w-3" /> {t("sampling.downloadCsv")}
                </Button>
              )}
            </div>
          </>
        )}
    </FullScreenDialog>
  );
}


function downloadSkippedCsv(data) {
  const rows = data.rows || [];
  const REASON_LABEL = {
    out_of_scope: "Mapped to a different FS head",
    unmapped_on_engagement: "Account not mapped on this engagement",
    missing_code: "No account code or name",
    bad_row: "Malformed row",
  };
  const header = ["Row #", "Account Code", "Account Name", "Voucher",
                   "Debit", "Credit", "Currently Mapped To FS", "Reason"];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([
      r.row_index || "",
      r.account_code || "",
      r.account_name || "",
      r.voucher_no || "",
      typeof r.debit === "number" ? r.debit : "",
      typeof r.credit === "number" ? r.credit : "",
      r.current_fs_mapping || "",
      REASON_LABEL[r.reason] || r.reason || "",
    ].map(escape).join(","));
  }
  // BOM so Excel opens as UTF-8
  const blob = new Blob(["\uFEFF" + lines.join("\n")],
                          { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  const safe = (data.source_file || "head-upload").replace(/[^\w.-]+/g, "_").slice(0, 60);
  a.href = URL.createObjectURL(blob);
  a.download = `${safe}_skipped.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export { SkippedRowsDialog };
